import React, { createContext, useContext, useEffect } from "react";
import { notification } from "antd";
import type { NotificationInstance } from "antd/es/notification/interface";
import type { NotificationPlacement } from "antd/es/notification/interface";
import { setGlobalNotificationApi } from "../utils/notification";

interface NotificationConfig {
  message: string;
  description?: string;
  duration?: number;
  placement?: NotificationPlacement;
}

const defaultConfig = {
  duration: 2,
  placement: "topRight" as NotificationPlacement,
};

const NotificationContext = createContext<NotificationInstance | null>(null);

export const useNotification = () => {
  const notificationApi = useContext(NotificationContext);
  if (!notificationApi) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }

  const showNotification = (
    type: "success" | "error" | "info" | "warning",
    config: NotificationConfig,
  ) => {
    notificationApi[type]({
      ...config,
      duration: config.duration ?? defaultConfig.duration,
      placement: config.placement ?? defaultConfig.placement,
    });
  };

  return {
    success: (config: NotificationConfig) =>
      showNotification("success", config),
    error: (config: NotificationConfig) => showNotification("error", config),
    info: (config: NotificationConfig) => showNotification("info", config),
    warning: (config: NotificationConfig) =>
      showNotification("warning", config),
  };
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notificationApi, contextHolder] = notification.useNotification();

  // Set the global notification API for use in sagas
  useEffect(() => {
    setGlobalNotificationApi(notificationApi);
  }, [notificationApi]);

  return (
    <NotificationContext.Provider value={notificationApi}>
      {contextHolder}
      {children}
    </NotificationContext.Provider>
  );
};
